import {
    Component,
    Inject,
    OnDestroy,
    OnInit,
    ChangeDetectionStrategy,
} from '@angular/core';
import { FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatCheckbox } from '@angular/material/checkbox';
import { MatChipsModule } from '@angular/material/chips';
import {
    MAT_DIALOG_DATA,
    MatDialogActions,
    MatDialogContent,
    MatDialogRef,
    MatDialogTitle,
} from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { IMaskModule } from 'angular-imask';
import { Observable, Subject, takeUntil, tap } from 'rxjs';
import { IMaskConstants } from '../../../../../../core/constants/imask';
import { CardTypeEnum } from '../../../../../../core/enums/commerce/cards-on-file/card-types';
import { CreateCardOnFileStatus } from '../../../../../../core/enums/commerce/cards-on-file/create-card-on-file-status';
import { nameOf } from '../../../../../../core/functions/string-helpers';
import { CreateCardOnFileRequest } from '../../../../../../core/models/commerce/cards-on-file/create-card-on-file-request';
import { CreateCardOnFileResponse } from '../../../../../../core/models/commerce/cards-on-file/create-card-on-file-response';
import { DropdownOption } from '../../../../../../core/models/dropdown-option';
import { FormGroupType } from '../../../../../../core/models/form-group-type';
import { RegExPattern } from '../../../../../../core/models/regexp-pattern/regex-pattern';
import { AlertService } from '../../../../../../core/services/alert/alert.service';
import { CreditCardService } from '../../../../../../core/services/commerce/credit-card/credit-card.service';
import { DropdownService } from '../../../../../../core/services/resources/dropdown.service';
import { CardTypeIconComponent } from '../../../../../../shared/components/card-type-icon/card-type-icon.component';
import { DraggableModalWrapperComponent } from '../../../../../../shared/components/modal/draggable-modal-wrapper/draggable-modal-wrapper.component';
import { RegexInputDirective } from '../../../../../../shared/directives/regex-input/regex-input.directive';
import { CardOnFileFormConstants } from '../shared/constants/card-on-file-form-constants';
import { CardOnFileValidationMessages } from '../shared/constants/card-on-file-validation-messages';
import { CardOnFileFormService } from '../shared/services/card-on-file-form.service';
import { CreateCardOnFileFormMessages } from './constants/create-card-on-file-form-messages';
import { CreateCardOnFileValidationMessages } from './constants/create-card-on-file-validation-messages';
import { passesLuhnCheck } from './helpers/credit-card-luhn';
import { CreateCardOnFileFormModalData } from './models/create-card-on-file-form-modal-data';
import { CreateCardOnFileFormModel } from './models/create-card-on-file-form-model';
import { CreateCardOnFileFormService } from './services/create-card-on-file-form.service';

@Component({
    selector: 'app-create-card-on-file-form-modal',
    templateUrl: './create-card-on-file-form-modal.component.html',
    styleUrl: '../shared/styles/card-on-file-form-modal.component.scss',
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
        MatCardModule,
        MatButtonModule,
        MatDialogContent,
        MatSelectModule,
        MatDialogActions,
        MatDialogTitle,
        MatAutocompleteModule,
        MatFormFieldModule,
        MatInputModule,
        MatChipsModule,
        MatIconModule,
        FormsModule,
        ReactiveFormsModule,
        RegexInputDirective,
        IMaskModule,
        MatCheckbox,
        DraggableModalWrapperComponent,
        CardTypeIconComponent,
    ],
})
export class CreateCardOnFileFormModalComponent implements OnInit, OnDestroy {
    private readonly destroy$ = new Subject<void>();

    maskOptions = IMaskConstants;
    regExPattern = RegExPattern;
    cardOnFileFormConstants = CardOnFileFormConstants;
    createCardOnFileValidationMessages = CreateCardOnFileValidationMessages;

    stateCodeOptions: DropdownOption[] = [];
    monthOptions: DropdownOption[] =
        this.dropdownService.getCreditCardMonthOptions();
    yearOptions: DropdownOption[] =
        this.dropdownService.getCreditCardYearOptions();

    /**
     * The form group for the card on file form.
     */
    form: FormGroup<FormGroupType<CreateCardOnFileFormModel>> =
        this.createCardOnFileFormService.getFormDefinition(
            Boolean(this.data.orderId),
        );

    /**
     * Holds the response of the create card on file operation.
     */
    createCardOnFileResponse: CreateCardOnFileResponse | null = null;

    /**
     * Indicates whether the card code input should be visible or masked.
     */
    showCardCode = false;

    constructor(
        @Inject(MAT_DIALOG_DATA)
        public data: CreateCardOnFileFormModalData,
        private readonly alertService: AlertService,
        private readonly dropdownService: DropdownService,
        private readonly creditCardService: CreditCardService,
        private readonly cardOnFileFormService: CardOnFileFormService,
        private readonly createCardOnFileFormService: CreateCardOnFileFormService,
        private readonly dialogRef: MatDialogRef<CreateCardOnFileFormModalComponent>,
    ) {}

    ngOnInit(): void {
        this.loadDropdownOptions();
        this.setCardAsDefaultIfOnlyStoredCard();
        this.setupFormListeners();
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    /**
     * The store for future use option is only displayed when a card on file is being added in the context of an order, as opposed to being
     * added from the user's profile without an associated order.
     *
     * @returns true if the store for future use option should be displayed, false otherwise.
     */
    displayStoreForFutureUseOption(): boolean {
        return Boolean(this.data.orderId);
    }

    /**
     * The option to set the card as the default payment method is only displayed when the card is being added without the context of an order (i.e. from the user's profile)
     * or when the card is being added in the context of an order but the user has chosen to store the card for future use.
     *
     * @return true if the option to set the card as the default payment method should be displayed, false otherwise.
     */
    displayIsDefaultOption(): boolean {
        return (
            !this.displayStoreForFutureUseOption() ||
            Boolean(this.form.controls.storeForFutureUse.value)
        );
    }

    getCardTypeFromNumber(): CardTypeEnum | undefined {
        const cardNumber = this.form.controls.cardNumber.value;

        if (!cardNumber || !passesLuhnCheck(cardNumber)) {
            return undefined;
        }

        if (this.createCardOnFileFormService.isMastercardNumber(cardNumber)) {
            return CardTypeEnum.Mastercard;
        }

        if (this.createCardOnFileFormService.isVisaNumber(cardNumber)) {
            return CardTypeEnum.Visa;
        }

        if (
            this.createCardOnFileFormService.isAmericanExpressNumber(cardNumber)
        ) {
            return CardTypeEnum.Amex;
        }

        return undefined;
    }

    toggleCardCodeVisibility(): void {
        this.showCardCode = !this.showCardCode;
    }

    getCardNumberError(): string | undefined {
        const errors = this.form.controls.cardNumber.errors;

        if (!errors) {
            return undefined;
        }

        const invalidLuhn = errors[
            nameOf<typeof CreateCardOnFileValidationMessages>(
                'cardNumberInvalidLuhn',
            )
        ] as string | undefined;

        if (invalidLuhn) {
            return invalidLuhn;
        }

        return errors[
            nameOf<typeof CreateCardOnFileValidationMessages>(
                'cardNumberNotAValidType',
            )
        ] as string | undefined;
    }

    getCardCodeError(): string | undefined {
        const errors = this.form.controls.cardCode.errors;

        if (!errors) {
            return undefined;
        }

        return errors[
            nameOf<typeof CreateCardOnFileValidationMessages>('cardCodeInvalid')
        ] as string | undefined;
    }

    getExpirationMonthError(): string | undefined {
        const errors = this.form.controls.expirationMonth.errors;

        if (!errors) {
            return undefined;
        }

        return errors[
            nameOf<typeof CardOnFileValidationMessages>(
                'expirationMonthExpired',
            )
        ] as string | undefined;
    }

    displayDuplicatePaymentInformationError(): boolean {
        return (
            this.createCardOnFileResponse?.status ===
            CreateCardOnFileStatus.DuplicatePaymentMethod
        );
    }

    /**
     * Handles the save action for the card on file form. Validates the form and, if valid, saves the card on file information to the API. Displays a
     * success alert and closes the modal upon successful save.
     *
     * If the save is successful, the dialog is closed with a result of the created card on file to indicate that a new card on file was created, which can be used
     * by the calling component to determine whether to refresh the list of cards on file after the dialog is closed.
     */
    onSave(): void {
        if (this.form.valid) {
            this.saveFormDataToApi()
                .pipe(
                    tap((response: CreateCardOnFileResponse) => {
                        this.createCardOnFileResponse = response;
                    }),
                    tap((response: CreateCardOnFileResponse) => {
                        if (
                            response.status === CreateCardOnFileStatus.Success
                        ) {
                            this.alertService.displaySuccessAlert(
                                CreateCardOnFileFormMessages.SUCCESS_ALERT_MESSAGE,
                            );

                            this.dialogRef.close(response.cardOnFile);
                        }
                    }),
                )
                .subscribe();
        }
    }

    /**
     * Handles the cancel action for the card on file form. Closes
     * the modal without saving any changes.
     *
     * Closing the dialog with a result of 'null' indicates that the operation was not completed, which can
     * be used by the calling component to determine whether to refresh the list of cards on file or not after
     * the dialog is closed.
     */
    onCancel(): void {
        this.dialogRef.close(null);
    }

    /**
     * Saves the card on file information to the API.
     *
     * @returns An observable that completes when the save operation is finished, returning the response from the create card on file API call.
     */
    private saveFormDataToApi(): Observable<CreateCardOnFileResponse> {
        return this.creditCardService.create(
            this.data.userId,
            this.generateRequestObject(),
        );
    }

    /**
     * Generates the request object to be sent to the API for creating a card on file based on the current form values.
     *
     * @return The create card on file request object generated from the form values.
     */
    private generateRequestObject(): CreateCardOnFileRequest {
        // Use raw value to ensure we include disabled controls
        return {
            ...this.form.getRawValue(),
            orderId: this.data.orderId,
        } as CreateCardOnFileRequest;
    }

    /**
     * Loads the dropdown options for the form.
     */
    private loadDropdownOptions(): void {
        this.dropdownService
            .getStateCodes()
            .pipe(
                takeUntil(this.destroy$),
                tap((options: DropdownOption[]) => {
                    this.stateCodeOptions = options;
                }),
            )
            .subscribe();
    }

    /**
     * Sets up listeners for form value changes to reset the create card on file status whenever the user modifies any form field.
     */
    private setupFormListeners(): void {
        this.form.valueChanges
            .pipe(
                takeUntil(this.destroy$),
                tap(() => {
                    this.createCardOnFileResponse = null;
                }),
            )
            .subscribe();

        this.form.controls.storeForFutureUse.valueChanges
            .pipe(
                takeUntil(this.destroy$),
                tap((storeForFutureUse: boolean | null) => {
                    // If the form control is disabled, then this indicates that the card that
                    // is being added, if chosen to be stored for future use, MUST be set as the
                    // default payment method.
                    if (this.form.controls.isDefault.disabled) {
                        return;
                    }

                    if (storeForFutureUse === false) {
                        // If the user unchecks the option to store for future use, we should
                        // also uncheck the option to set as default, since a card that is not
                        // stored for future use cannot be set as the default payment method.
                        this.form.controls.isDefault.patchValue(false, {
                            emitEvent: false,
                        });
                    }
                }),
            )
            .subscribe();

        this.cardOnFileFormService.setupExpirationYearListener(
            this.form,
            this.destroy$,
        );
    }

    /**
     * If the user has no other stored cards on file, the card being added must be set as
     * the default payment method. This function checks if there are any other stored cards
     * on file and, if not, sets the card being added as the default payment method.
     */
    private setCardAsDefaultIfOnlyStoredCard(): void {
        // If there are no other stored cards on file, set this card as the default payment method
        const hasNoOtherStoredCardsOnFile =
            this.data.cardsOnFile.filter((card) => card.isStored).length === 0;

        if (hasNoOtherStoredCardsOnFile) {
            this.cardOnFileFormService.setCardAsDefault(this.form);
        }
    }
}
